import { Skeleton } from '@mui/material';
import { MetricCard, Text } from '@fragments';
import {
    IconCashBanknote,
    IconCoin,
    IconReceiptRefund,
    IconShieldDollar,
    IconReceipt,
    IconCalculator,
    IconAlertTriangle,
} from '@tabler/icons-react';

import './styles.scss';

function toReais(cents) {
    if (cents == null) return 0;
    return Math.round(Number(cents)) / 100;
}

// Sentiment thresholds for refund / chargeback share-of-sales.
// `green` is the upper bound for "good"; `red` is the lower bound for "bad";
// anything between is treated as a warning.
function sentimentByThreshold(percentile, green, red) {
    const p = Number(percentile) || 0;
    if (p < green) return 'positive';
    if (p >= red) return 'negative';
    return 'warning';
}

export default function MetricsSection({ report, reportLoading }) {
    const summary = report?.summary;
    const revenue = summary?.revenue;

    return (
        <div className="insights-metrics">
            <Text.h4 contrast={7} className="insights-metrics__heading mb-2">
                Resumo do período
            </Text.h4>

            {reportLoading && !report ? (
                <div className="insights-metrics__row">
                    {[0, 1, 2, 3, 4, 5, 6].map(i => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            height={140}
                            className="insights-metrics__skeleton"
                        />
                    ))}
                </div>
            ) : (
                <div
                    className={`insights-metrics__row${reportLoading ? ' insights-metrics__row--reloading' : ''}`}
                >
                    <MetricCard
                        title="Total de Vendas"
                        leftIcon={<IconReceipt size={15} />}
                        value={summary?.salesTotal ?? 0}
                        variant="number"
                        description="Quantidade de cobranças pagas no período."
                        loading={reportLoading}
                    />
                    <MetricCard
                        title="Receita bruta"
                        leftIcon={<IconCashBanknote size={15} />}
                        value={toReais(revenue?.total)}
                        variant="money"
                        description="Soma das cobranças pagas, reembolsadas ou revertidas no período, incluindo as taxas da plataforma."
                        loading={reportLoading}
                    />
                    <MetricCard
                        title="Receita líquida"
                        leftIcon={<IconCoin size={15} />}
                        value={toReais(revenue?.liquid)}
                        variant="money"
                        description="Soma das comissões de todos os recebedores."
                        loading={reportLoading}
                    />
                    <MetricCard
                        title="Sua comissão"
                        leftIcon={<IconShieldDollar size={15} />}
                        value={toReais(revenue?.commission)}
                        variant="money"
                        description="Comissão atribuída a você."
                        loading={reportLoading}
                    />
                    <MetricCard
                        title="Ticket médio"
                        leftIcon={<IconCalculator size={15} />}
                        value={toReais(revenue?.averageTicket)}
                        variant="money"
                        description="Receita bruta / quantidade de cobranças pagas."
                        loading={reportLoading}
                    />
                    <MetricCard
                        title="Reembolsos"
                        leftIcon={<IconReceiptRefund size={15} />}
                        value={toReais(summary?.refund?.amount)}
                        variant="money"
                        note={`${Math.round((summary?.refund?.percentile ?? 0) * 10) / 10}% das vendas no período`}
                        noteSentiment={sentimentByThreshold(summary?.refund?.percentile, 4.5, 6)}
                        description="Valor reembolsado e representação sobre o total de vendas."
                        loading={reportLoading}
                    />
                    <MetricCard
                        title="Chargebacks"
                        leftIcon={<IconAlertTriangle size={15} />}
                        value={toReais(summary?.chargeback?.amount)}
                        variant="money"
                        note={`${Math.round((summary?.chargeback?.percentile ?? 0) * 10) / 10}% das vendas no período`}
                        noteSentiment={sentimentByThreshold(summary?.chargeback?.percentile, 1, 1.5)}
                        description="Valor contestado e representação sobre o total de vendas."
                        loading={reportLoading}
                    />
                </div>
            )}
        </div>
    );
}
